import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme/index';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import { getCartItemTotal } from '../utils/pricing';

function PaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [payuData, setPayuData] = useState(null);
  const formRef = useRef(null);

  const { totalINR: passedTotalINR, cartItems, selectedAddressId, shippingMethod, shippingCost, estimatedDelivery } = location.state || {};

  // Recalculate totals applying bulk offers
  const subtotalINR = Math.max(0, (cartItems || []).reduce((sum, item) => sum + getCartItemTotal(item).total, 0));
  const gstAmount = Math.round((cartItems || []).reduce((sum, item) => {
    const itemTotal = getCartItemTotal(item).total;
    const itemGst = item.gst || 5;
    return sum + (itemTotal * (itemGst / 100));
  }, 0));
  const totalINR = Math.round(subtotalINR + gstAmount + (Number(shippingCost) || 0));
  const [expiresAt, setExpiresAt] = useState(null);
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    if (!totalINR || !cartItems || cartItems.length === 0) {
      navigate('/cart');
      return;
    }

    const createPayUOrder = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/payments/create-order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: user.id,
            amount: totalINR,
            cartItems: cartItems,
            addressId: selectedAddressId,
            shippingMethod: shippingMethod,
            shippingCost: Number(shippingCost) || 0,
            estimatedDelivery: estimatedDelivery,
            userEmail: user.email,
            userPhone: user.phone,
          }),
        });

        const data = await response.json();
        if (response.ok) {
          setPayuData(data.data);
          if (data.data?.reservationExpiresAt) {
            setExpiresAt(new Date(data.data.reservationExpiresAt));
          }
          // Auto-submit the PayU form after a short delay
          setTimeout(() => {
            if (formRef.current) {
              formRef.current.submit();
            }
          }, 1000);
        } else {
          alert('Error creating order: ' + data.message);
          navigate('/checkout');
        }
      } catch (err) {
        alert('Connection error: ' + err.message);
        navigate('/checkout');
      } finally {
        setLoading(false);
      }
    };

    createPayUOrder();
  }, [totalINR, cartItems, selectedAddressId, shippingMethod, shippingCost, estimatedDelivery, token, user, navigate]);

  useEffect(() => {
    if (!expiresAt) return;

    const updateTimer = () => {
      const diffMs = new Date(expiresAt).getTime() - Date.now();
      if (diffMs <= 0) {
        setTimeLeft('Expired');
        return;
      }
      const minutes = Math.floor(diffMs / 60000);
      const seconds = Math.floor((diffMs % 60000) / 1000);
      setTimeLeft(`${minutes}m ${seconds}s`);
    };

    updateTimer();
    const timerId = window.setInterval(updateTimer, 1000);
    return () => window.clearInterval(timerId);
  }, [expiresAt]);

  if (loading || !payuData) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 1.5, repeat: Infinity }} className="text-center">
          <div style={{ fontSize: '4rem', marginBottom: spacing.lg }}>⏳</div>
          <p style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.text.primary }}>Redirecting to PayU Payment Gateway...</p>
          <p style={{ fontSize: '0.9rem', color: colors.text.secondary, marginTop: spacing.md }}>Please do not refresh or go back</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <motion.div
        style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          padding: spacing.xl,
          boxShadow: shadows.lg,
          maxWidth: '420px',
          width: '100%',
          textAlign: 'center',
        }}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div style={{ fontSize: '4rem', marginBottom: spacing.lg }}>🔒</div>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 900, color: colors.text.primary, margin: 0, marginBottom: spacing.sm }}>
          Redirecting to PayU
        </h2>
        <p style={{ fontSize: '0.95rem', color: colors.text.secondary, marginBottom: spacing.lg }}>
          You are being securely redirected to PayU's payment gateway to complete your payment of <strong>₹{totalINR}</strong>
        </p>

        <div style={{ padding: spacing.md, backgroundColor: colors.primary[25], borderRadius: '0.5rem', marginBottom: spacing.lg }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: colors.primary[700] }}>
            <strong>Order:</strong> #{payuData.orderId}
          </p>
          <p style={{ margin: `${spacing.xs} 0 0`, fontSize: '0.85rem', color: colors.primary[700] }}>
            <strong>Amount:</strong> ₹{totalINR}
          </p>
          {timeLeft && (
            <p style={{ margin: `${spacing.xs} 0 0`, fontSize: '0.85rem', color: colors.slate?.[600] || '#475569' }}>
              <strong>Reservation expires in:</strong> {timeLeft}
            </p>
          )}
        </div>

        <form
          ref={formRef}
          action={payuData.baseUrl}
          method="post"
          style={{ display: 'none' }}
        >
          <input type="hidden" name="key" value={payuData.key} />
          <input type="hidden" name="txnid" value={payuData.txnid} />
          <input type="hidden" name="amount" value={String(Number(payuData.amount).toFixed(2))} />
          <input type="hidden" name="productinfo" value={payuData.productinfo} />
          <input type="hidden" name="firstname" value={payuData.firstname} />
          <input type="hidden" name="email" value={payuData.email} />
          <input type="hidden" name="phone" value={payuData.phone} />
          <input type="hidden" name="surl" value={payuData.surl} />
          <input type="hidden" name="furl" value={payuData.furl} />
          <input type="hidden" name="hash" value={payuData.hash} />
          <input type="hidden" name="udf1" value={payuData.udf1} />
        </form>

        <motion.button
          onClick={() => formRef.current?.submit()}
          style={{
            width: '100%',
            padding: spacing.lg,
            backgroundColor: colors.primary[600],
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontWeight: 700,
            fontSize: '1rem',
            cursor: 'pointer',
          }}
          whileHover={{ backgroundColor: colors.primary[700] }}
          whileTap={{ scale: 0.95 }}
        >
          💳 Proceed to PayU
        </motion.button>

        <p style={{ fontSize: '0.8rem', color: colors.text.secondary, marginTop: spacing.md }}>
          Click the button above if you are not redirected automatically
        </p>
      </motion.div>
    </div>
  );
}

export default PaymentPage;